/**
 * Pure utilities — no database or browser dependencies.
 *
 * Includes: createTaskDefaults, nextIndex, distributeIndexes, label maps.
 */

import { ObjectType, ObjectStatus, StartAtType, type DbObject } from "./db-types.js"

// ---------------------------------------------------------------------------
// Human-readable labels for enums (used in analytics + logging)
// ---------------------------------------------------------------------------

const objectTypeLabels: Record<number, string> = {
  [ObjectType.Checklist]: "checklist",
  [ObjectType.Subtask]: "subtask",
  [ObjectType.Task]: "task",
  [ObjectType.Section]: "section",
  [ObjectType.Project]: "project",
  [ObjectType.Area]: "area",
  [ObjectType.List]: "list",
  [ObjectType.ContentBlock]: "content_block",
  [ObjectType.Root]: "root",
}

const objectStatusLabels: Record<number, string> = {
  [ObjectStatus.Hidden]: "hidden",
  [ObjectStatus.Canceled]: "canceled",
  [ObjectStatus.Unfinished]: "unfinished",
  [ObjectStatus.Completed]: "completed",
}

const startAtTypeLabels: Record<number, string> = {
  [StartAtType.Default]: "default",
  [StartAtType.Someday]: "someday",
}

/** Convert ObjectType enum → readable string for analytics */
export function objectTypeLabel(type: ObjectType): string {
  return objectTypeLabels[type] ?? `unknown_${type}`
}

/** Convert ObjectStatus enum → readable string for analytics */
export function objectStatusLabel(status: ObjectStatus): string {
  return objectStatusLabels[status] ?? `unknown_${status}`
}

/** Convert StartAtType enum → readable string for analytics */
export function startAtTypeLabel(type: StartAtType): string {
  return startAtTypeLabels[type] ?? `unknown_${type}`
}

// ---------------------------------------------------------------------------
// createTaskDefaults — pure object factory
// ---------------------------------------------------------------------------

/**
 * Create a new task/object with sensible defaults.
 */
export function createTaskDefaults(
  overrides: Partial<DbObject> & { id: string },
): DbObject {
  return {
    type: ObjectType.Task,
    title: "",
    note: "",
    parentPath: "",
    index: 0,
    status: ObjectStatus.Unfinished,
    tags: [],
    isDeleted: 0,
    owner: "",
    realmId: "",
    createdAt: "",
    createdBy: "",
    fullTextIndex: [],
    ...overrides,
  }
}

// ---------------------------------------------------------------------------
// Fractional index utilities
// ---------------------------------------------------------------------------

const MIN_INDEX_GAP = 1e-10

export let indexNeedsRepair = false

/**
 * Calculate a new index between two neighbors.
 * If both are provided, returns the midpoint.
 * If only start, returns start + 1.
 * If only end, returns end - 1.
 */
export function nextIndex(
  start: number | undefined,
  end: number | undefined,
): number {
  indexNeedsRepair = false

  if (start != null && end != null) {
    const gap = Math.abs(end - start)
    if (gap < MIN_INDEX_GAP) {
      indexNeedsRepair = true
      console.warn(
        `[indexUtils] precision collapse: start=${start}, end=${end}, gap=${gap}. Forcing offset.`,
      )
      return start + 1
    }
    return start + (end - start) / 2
  }
  if (start != null) {
    return start + 1
  }
  if (end != null) {
    return end - 1
  }
  return 0
}

/**
 * Calculate indexes for multiple items being inserted between two boundaries.
 */
export function distributeIndexes(
  start: number | undefined,
  end: number | undefined,
  count: number,
): number[] {
  if (count === 0) return []
  indexNeedsRepair = false

  const indexes: number[] = []

  if (start != null && end != null) {
    const gap = Math.abs(end - start)
    if (gap < MIN_INDEX_GAP) {
      indexNeedsRepair = true
      console.warn(
        `[indexUtils] distributeIndexes precision collapse: start=${start}, end=${end}. Using offset.`,
      )
      for (let i = 0; i < count; i++) {
        indexes.push(start + (i + 1))
      }
      return indexes
    }
    const step = (end - start) / (count + 1)
    for (let i = 1; i <= count; i++) {
      indexes.push(start + step * i)
    }
  } else if (start != null) {
    for (let i = 0; i < count; i++) {
      indexes.push(start + (i + 1))
    }
  } else if (end != null) {
    for (let i = 0; i < count; i++) {
      indexes.push(end - (count - i))
    }
  } else {
    for (let i = 0; i < count; i++) {
      indexes.push(i)
    }
  }

  return indexes
}

// ---------------------------------------------------------------------------
// Date normalization
// ---------------------------------------------------------------------------

/**
 * The range a number must lie in to be read as "ms since the epoch": 1e11 (1973-03-03) up to 1e14
 * (year 5138). Anything below is not a date a task manager stores - `0`, an 8-digit `20261007`, or
 * 10-digit Unix *seconds* (which would read as a day in January 1970) - and is rejected rather than
 * turned into a date in 1970.
 */
const PLAUSIBLE_MS_MIN = 1e11
const PLAUSIBLE_MS_MAX = 1e14

/** Is `ms` a plausible epoch-millisecond value (see PLAUSIBLE_MS_MIN)? */
export function isPlausibleMs(ms: number): boolean {
  return Number.isFinite(ms) && ms >= PLAUSIBLE_MS_MIN && ms < PLAUSIBLE_MS_MAX
}

/**
 * Normalize a date value to a stringified UTC ms timestamp.
 * Accepts: ISO string, unix-ms number, or already-correct ms-timestamp string.
 * Returns `undefined` for falsy / unparseable input, and for a number that is not a plausible
 * epoch-millisecond value (`0`, `20261007`, Unix seconds).
 *
 * All dates in Dexie are stored as `dayjs().utc().valueOf().toString()` → "1739577600000".
 */
export function toMsTimestamp(value: string | number | undefined | null): string | undefined {
  if (value === undefined || value === null || value === "") return undefined
  if (typeof value === "string" && value.trim() === "") return undefined
  const num = Number(value)
  if (Number.isFinite(num)) return isPlausibleMs(num) ? String(num) : undefined
  if (typeof value === "string") {
    const parsed = Date.parse(value)
    if (!isNaN(parsed)) return String(parsed)
  }
  return undefined
}

/**
 * Read a stored date tolerantly: the canonical stringified UTC ms ("1739577600000"), a ms number, a
 * Date, or an ISO 8601 string. Returns ms since the epoch, or `undefined` for empty / unparseable
 * input and for a number that is not a plausible epoch-millisecond value (`"0"`, `"20261007"`,
 * Unix seconds - see isPlausibleMs).
 *
 * Writers must store `toMsTimestamp(...)` / `toDbDate(...)` (see above). The app's own fields
 * `dueAt`, `fireAt`, `statusAt` and the startAt of the schedule are read as numbers, and rows written
 * before every writer stored ms (an ISO `dueAt` from a keyboard shortcut, an ISO `createdAt` /
 * `updatedAt` from several paths) are still in the database and synced to every client: every reader
 * goes through this function instead of `Number(value)`, which turns such a row into NaN.
 */
export function parseDbDate(value: string | number | Date | undefined | null): number | undefined {
  if (value === undefined || value === null || value === "") return undefined
  if (value instanceof Date) {
    const t = value.getTime()
    return Number.isFinite(t) ? t : undefined
  }
  if (typeof value === "number") return isPlausibleMs(value) ? value : undefined
  const trimmed = value.trim()
  if (trimmed === "") return undefined
  const num = Number(trimmed)
  if (Number.isFinite(num)) return isPlausibleMs(num) ? num : undefined
  const parsed = Date.parse(trimmed)
  return Number.isNaN(parsed) ? undefined : parsed
}

/** A stored date as ISO 8601 UTC (`2026-10-07T08:30:00.000Z`), or `undefined` when it has none. */
export function toIsoTimestamp(value: string | number | Date | undefined | null): string | undefined {
  const ms = parseDbDate(value)
  if (ms === undefined) return undefined
  const date = new Date(ms)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/** A stored date as its UTC calendar day (`2026-10-07`), or `undefined` when it has none. */
export function toIsoDate(value: string | number | Date | undefined | null): string | undefined {
  return toIsoTimestamp(value)?.slice(0, 10)
}

/**
 * A UUID-shaped id (version 5 layout) derived from `base` and `label`: the same pair always gives
 * the same id. Used where one request creates several objects and a retry must find each of them
 * again instead of creating them twice (execute_plan: one id per step, reminder and checklist row).
 */
export async function deriveChildId(base: string, label: string): Promise<string> {
  const bytes = new Uint8Array(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`totodo-child-id:v1\n${base}\n${label}`)),
  )
  bytes[6] = (bytes[6]! & 0x0f) | 0x50
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const h = Array.from(bytes.subarray(0, 16), (b) => b.toString(16).padStart(2, "0")).join("")
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`
}
